import React, { useEffect, useState } from "react";
import { adminAxios } from "../api";
import { toast } from "sonner";
import { Loader2, Phone, Trash2, Check } from "lucide-react";

const LeadsAdmin = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      setLeads((await adminAxios.get("/leads", { params })).data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const markContacted = async (l) => {
    try {
      await adminAxios.patch(`/leads/${l.id}/status`, { status: l.status === "contacted" ? "new" : "contacted" });
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (l) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await adminAxios.delete(`/leads/${l.id}`); toast.success("Lead deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Leads</h1>
      <p className="text-slate-500 mb-6">Callback requests captured from the Call Us popup</p>

      <div className="flex gap-2 mb-4">
        {[["", "All"], ["new", "New"], ["contacted", "Contacted"]].map(([v, label]) => (
          <button
            key={v}
            data-testid={`leads-filter-${v || "all"}`}
            onClick={() => setFilter(v)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${filter === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-slate-400 animate-spin" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">Source</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Received</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {leads.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-800">{l.name || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-3">
                    <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 font-medium text-slate-800 hover:text-amber-600">
                      <Phone className="w-3.5 h-3.5" /> {l.phone}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{l.source}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${l.status === "contacted" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{l.status}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{new Date(l.created_at).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        data-testid={`lead-toggle-${l.id}`}
                        onClick={() => markContacted(l)}
                        title={l.status === "contacted" ? "Mark as new" : "Mark contacted"}
                        className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-100 text-slate-600"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`lead-delete-${l.id}`}
                        onClick={() => remove(l)}
                        title="Delete"
                        className="p-1.5 rounded-md border border-slate-200 hover:bg-red-50 hover:border-red-200 text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {leads.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400">No leads yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LeadsAdmin;
